/**
 * Job detail template — page chrome + inner job details copy (#careeerDetails).
 * Multipart Chakra theme: useMultiStyleConfig('JobDetailsPage', {}).
 */

/** Body / rich-description tokens shared by detail column. */
const JOB_DETAILS_BODY_TEXT_SX = {
  fontSize: '14px',
  lineHeight: '22px',
  fontWeight: 400,
  fontFamily: 'body',
  fontStyle: 'normal',
  textAlign: 'left',
  textIndent: 0,
  color: 'black',
};

const JOB_DESCRIPTION_HTML_SX = {
  ...JOB_DETAILS_BODY_TEXT_SX,
  verticalAlign: 'top',
  'p, li, div': {
    fontSize: {base: '12px', md: '14px'},
    lineHeight: '22px',
    fontWeight: 400,
    fontFamily: 'body',
    textAlign: 'left',
    textIndent: 0,
    verticalAlign: 'top',
  },
  p: {
    mb: 4,
  },
  'h2, h3, h4': {
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '22px',
    fontFamily: 'body',
    fontStyle: 'normal',
    textAlign: 'left',
    textTransform: 'none',
    letterSpacing: 'normal',
    mt: 6,
    mb: {base: '13px', md: '32px'},
  },
  ul: {
    pl: 5,
    mb: 4,
  },
  li: {
    mb: 1,
  },
  'strong, b': {
    fontWeight: 700,
  },
};

const JobDetailsPage = {
  parts: [
    'pageRoot',
    'pageContainer',
    'heroCard',
    'breadcrumbRow',
    'backButton',
    'errorOuter',
    'errorMessage',
    'errorBackButton',
    'detailArea',
    'applySection',
    'applyInner',
    'viewAllJobsButton',
    'jobDetailsRoot',
    'jobTitle',
    'metaStack',
    'metaRow',
    'metaLabel',
    'metaValue',
    'detailDivider',
    'bodyStack',
    'bodyText',
    'bulletList',
    'bulletListItem',
    'descriptionBox',
    'emptyStateBox',
    'emptyStateTitle',
    'emptyStateBody',
    'loadingRoot',
    'loadingTitleSkeleton',
    'loadingMetaStack',
    'loadingMetaRow',
    'loadingLabelSkeleton',
    'loadingValueSkeleton',
    'loadingBodySkeleton',
  ],
  baseStyle: {
    pageRoot: {
      bg: 'white',
      minH: '100vh',
    },
    pageContainer: {
      maxW: 'container.xxl',
      px: {base: '20px', lg: '80px'},
      width: '100%',
    },
    heroCard: {
      bg: 'white',
      py: {base: 5, md: 5},
      boxShadow: 'sm',
    },
    breadcrumbRow: {
      mb: 4,
    },
    backButton: {
      variant: 'outline',
      borderColor: 'black',
      borderWidth: '1px',
      borderRadius: 0,
      bg: 'white',
      color: 'black',
      fontWeight: '700',
      fontFamily: 'body',
      fontSize: 'xs',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      lineHeight: '14px',
      pt: {base: '6px', lg: '6px'},
      pr: {base: '5px', lg: '16px'},
      pb: {base: '6px', lg: '6px'},
      pl: {base: '16px', lg: '16px'},
      h: 'auto',
      minH: 'unset',
      display: 'inline-flex',
      alignItems: 'center',
      iconSpacing: 0,
      height: 'unset',
      _hover: {
        bg: 'white',
        color: 'black',
        borderColor: 'black',
      },
      sx: {
        span: {
          fontSize: '11px',
          lineHeight: '14px',
          fontWeight: '700',
        },
      },
    },
    errorOuter: {
      maxW: '750px',
      mx: 'auto',
      w: '100%',
      textAlign: 'center',
      py: 10,
    },
    errorMessage: {
      color: 'red.500',
      mb: 4,
    },
    errorBackButton: {},
    detailArea: {
      className: 'detail-area',
      maxW: '750px',
      mx: 'auto',
      w: '100%',
    },
    applySection: {
      className: 'apply-careers',
      as: 'section',
      w: '100%',
      bg: '#ebebeb',
      pt: {base: '18px', md: '28px', lg: '45px'},
      pb: {base: '41px', md: '64px', lg: '53px'},
    },
    applyInner: {
      maxW: '552px',
      mx: 'auto',
      w: '100%',
      px: {base: '15px', md: 0},
    },
    viewAllJobsButton: {
      mt: 8,
      size: 'lg',
      width: 'full',
      borderRadius: 0,
      bg: 'black',
      color: 'white',
      fontWeight: 'normal',
      textTransform: 'uppercase',
      letterSpacing: 'wider',
      _hover: {bg: 'gray.800'},
    },
    jobDetailsRoot: {
      marginBottom: {base: '52px', md: '57px'},
    },
    jobTitle: {
      className: 'job-title',
      as: 'h1',
      fontSize: '24px',
      lineHeight: '28px',
      pt: '20px',
      mb: {base: 6, md: 8},
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      textAlign: 'center',
      fontFamily: 'body',
      fontWeight: 700,
      fontStyle: 'normal',
      color: 'black',
    },
    metaStack: {
      align: 'stretch',
      spacing: 4,
      mb: {base: 6, md: 8},
    },
    metaRow: {
      align: 'flex-start',
      flexDir: 'row',
      flexWrap: 'nowrap',
      w: 'full',
    },
    metaLabel: {
      fontSize: {base: '12px', md: '14px'},
      lineHeight: '22px',
      fontFamily: 'body',
      fontStyle: 'normal',
      textAlign: 'left',
      textIndent: 0,
      verticalAlign: 'top',
      color: 'black',
      fontWeight: 700,
      flexShrink: 0,
      minW: {base: '140px', sm: '180px'},
      sx: {
        'p': {
          marginBottom: {base: '0', md: '0'},
        },
      },
    },
    metaValue: {
      fontSize: {base: '12px', md: '14px'},
      lineHeight: '22px',
      fontFamily: 'body',
      fontStyle: 'normal',
      textAlign: 'left',
      textIndent: 0,
      verticalAlign: 'top',
      color: 'black',
      fontWeight: 400,
      flex: '1 1 0%',
      minW: 0,
      sx: {
        'p': {
          marginBottom: {base: '0', md: '0'},
        },
      },
    },
    detailDivider: {
      borderColor: 'gray.200',
      mb: {base: 6, md: 8},
    },
    bodyStack: {
      spacing: 6,
      align: 'stretch',
      w: 'full',
      sx: JOB_DETAILS_BODY_TEXT_SX,
    },
    bodyText: {
      sx: JOB_DETAILS_BODY_TEXT_SX,
    },
    bulletList: {
      as: 'ul',
      pl: 5,
      sx: {'& li': {verticalAlign: 'top'}},
    },
    bulletListItem: {
      as: 'li',
      mb: 2,
    },
    descriptionBox: {
      className: 'job-description',
      sx: JOB_DESCRIPTION_HTML_SX,
    },
    emptyStateBox: {
      textAlign: 'center',
      py: 10,
    },
    emptyStateTitle: {
      as: 'h2',
      size: 'lg',
      mb: 4,
    },
    emptyStateBody: {
      color: 'gray.600',
    },
    loadingRoot: {},
    loadingTitleSkeleton: {
      height: '28px',
      width: '75%',
      maxW: '560px',
      mb: 8,
      mt: '20px',
    },
    loadingMetaStack: {
      align: 'stretch',
      spacing: 4,
      mb: 8,
    },
    loadingMetaRow: {
      align: 'flex-start',
      spacing: 8,
    },
    loadingLabelSkeleton: {
      height: '22px',
      width: '160px',
      flexShrink: 0,
    },
    loadingValueSkeleton: {
      height: '22px',
      flex: 1,
    },
    loadingBodySkeleton: {
      noOfLines: 10,
      spacing: 3,
    },
  },
};

export default JobDetailsPage;
